import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Icon, TextField, Accordion, ButtonGroup } from 'components/ui';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { Check, AlertCircle } from 'react-feather';
import { REACT_QUERY_STATUS } from 'constants/config';
import DebouncedTextArea from 'components/shared/debouncedTextArea';
import Select from 'components/shared/select';
import Error from 'components/InPersonTraining/shared/Error';
import {
  useGetReadinessSkillsCategories,
  useCreateReadinessSkill,
  useUpdateReadinessSkill,
  useCreateReadinessSkillCategory,
} from '../queries/readinessSkills';
import DeleteModal from './DeleteModal';

const SkillSlider = ({
  handleClose,
  chosenSkill,
  setChosenSkill,
  setBannerProperties,
  skillSliderOpen,
  editSkillSlider,
  openEditSkillSlider,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(null);
  const skillNameRef = useRef();
  const { mutate: createMutate, status, error } = useCreateReadinessSkill();
  const queryClient = useQueryClient();
  const [hasSaveError, setHasSaveError] = useState(true);
  const [hasNameError, setHasNameError] = useState(false);
  const [hasUniqueError, setHasUniqueError] = useState(false);
  const [addCategory, setAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(false);
  const [successText, setSuccessText] = useState(false);
  const [errorText, setErrorText] = useState(false);
  const [categoryCreationError, setCategoryCreationError] = useState(false);
  const [deleteSkill, setDeleteSkill] = useState(false);
  const [saveChangesError, setSaveChangesError] = useState(false);
  const exceptionError =
    error?.response?.errors &&
    error?.response?.errors[0]?.extensions?.exception?.name;
  const [editSkillClicked, setEditSkillClicked] = useState(false);
  const [checked, setChecked] = useState(false);
  const {
    mutate: updateMutate,
    status: updateStatus,
    error: updateError,
    reset,
  } = useUpdateReadinessSkill({
    id: '',
    name,
    description,
    readinessSkillCategoryId: category ? parseInt(category, 10) : null,
  });
  const uniqueExceptionError =
    updateError?.response?.errors &&
    updateError?.response?.errors[0]?.extensions?.exception?.name;
  const {
    mutate: createCategoryMutate,
    status: createCategoryStatus,
    reset: createCategoryReset,
  } = useCreateReadinessSkillCategory({
    name: newCategoryName,
  });

  const saveChanges = async () => {
    setHasSaveError(false);
    setHasUniqueError(false);
    if (name?.length > 255) return;
    await createMutate(
      {
        name,
        description,
        readinessSkillCategoryId: category ? parseInt(category, 10) : null,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('get-readiness-skills');
        },
      },
    );
  };

  const { data: readinessSkillCategoryData } = useGetReadinessSkillsCategories({
    limit: 100,
    offset: 0,
  });

  const skillsCategories = useMemo(() => {
    return (
      readinessSkillCategoryData?.readinessSkillsCategories
        ?.readinessSkillCategories ?? []
    );
  }, [readinessSkillCategoryData]);

  async function handleDelete() {
    setDeleteSkill(true);
  }

  const closeDeleteModal = () => {
    setDeleteSkill(false);
    handleClose();
    setChosenSkill({});
  };

  useEffect(() => {
    reset();
    setEditSkillClicked(false);
    setAddCategory(false);
    setNewCategoryName('');
    setSaveChangesError(false);
    if (chosenSkill.name) {
      setName(chosenSkill.name);
      setDescription(chosenSkill.description);
      setCategory({
        id: chosenSkill.categoryId,
        name: chosenSkill.categoryName,
      });
    }
  }, [chosenSkill, reset]);

  const handleSelectChange = e => {
    setSaveChangesError(false);
    setErrorText(false);
    createCategoryReset();

    if (e.target.value === '0') {
      setCategory({ id: '0' });
      setAddCategory(true);
      setCategoryCreationError(false);
      setNewCategoryName('');
      setSelectedCategory(false);
    } else {
      setAddCategory(false);
      setCategoryCreationError(false);
      setNewCategoryName('');
      const matchedCategoryName = skillsCategories.find(
        option => option.id === parseInt(e.target.value, 10),
      )?.name;
      setCategory({
        id: parseInt(e.target.value, 10),
        name: matchedCategoryName,
      });
      setSelectedCategory(true);
    }
  };

  async function handleEdit() {
    if (newCategoryName) {
      await createCategoryMutate(
        {
          name: newCategoryName,
        },
        {
          onSettled: data => {
            updateMutate(
              {
                id: chosenSkill.id,
                name,
                description,
                readinessSkillCategoryId:
                  data?.createReadinessSkillCategory?.id,
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries('get-readiness-skills');
                },
              },
            );
            setCategory(data?.createReadinessSkillCategory);
          },
          onSuccess: () => {
            queryClient.invalidateQueries('get-readiness-skills-categories');
            setNewCategoryName('');
          },
        },
      );
    } else if (chosenSkill.id) {
      try {
        await updateMutate(
          {
            id: chosenSkill.id,
            name,
            description,
            readinessSkillCategoryId: category?.id,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries('get-readiness-skills');
            },
          },
        );
        setEditSkillClicked(false);
      } catch (err) {
        console.error(err);
      }
    } else {
      setHasSaveError(false);
      setHasUniqueError(false);
      if (name?.length > 255) return;
      createMutate(
        {
          name,
          description,
          readinessSkillCategoryId: category ? parseInt(category, 10) : null,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('get-readiness-skills');
          },
        },
      );
    }
  }

  useEffect(() => {
    if (status === REACT_QUERY_STATUS.SUCCESS) {
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_skill_created', {
                skill_name: name,
              }),
            }}
          />
        ),
        state: 'success',
      });
      handleClose();
    }
    if (status === REACT_QUERY_STATUS.ERROR && !hasSaveError) {
      if (exceptionError === 'SequelizeUniqueConstraintError') {
        setHasUniqueError(true);
      } else {
        setHasSaveError(true);
        setBannerProperties({
          message: (
            <p
              dangerouslySetInnerHTML={{
                __html: t('skills.banner_skill_error', {
                  skill_name: name,
                }),
              }}
            />
          ),
          state: 'error',
        });
      }
    }
    if (updateStatus === REACT_QUERY_STATUS.SUCCESS) {
      setSuccessText(true);
    }
    if (updateStatus === REACT_QUERY_STATUS.ERROR) {
      if (uniqueExceptionError === 'SequelizeUniqueConstraintError') {
        setEditSkillClicked(true);
        setHasUniqueError(true);
        setSaveChangesError(true);
      } else {
        setErrorText(true);
      }
    }
    if (createCategoryStatus === REACT_QUERY_STATUS.ERROR) {
      setCategoryCreationError(true);
      setSaveChangesError(true);
    }
    if (createCategoryStatus === REACT_QUERY_STATUS.SUCCESS && addCategory) {
      setEditSkillClicked(false);
      setAddCategory(false);
    }

    if (openEditSkillSlider && !checked) {
      setChecked(true);
      setEditSkillClicked(true);
    }
  }, [
    status,
    updateStatus,
    chosenSkill.name,
    handleClose,
    setChosenSkill,
    name,
    setBannerProperties,
    createCategoryStatus,
    hasSaveError,
    exceptionError,
    uniqueExceptionError,
    openEditSkillSlider,
    checked,
    addCategory,
  ]);

  useEffect(() => {
    if (updateStatus !== REACT_QUERY_STATUS.SUCCESS) {
      setSuccessText(false);
    }
  }, [updateStatus]);

  return (
    <>
      {editSkillSlider && (
        <div className="SkillsLibrary-editSlider w-360 d-f fd-c pos-a top-0 right-0 h-100 bg-white border-left border-gray-light">
          <div className="d-f jc-sb h-60 w-100p ai-c border-bottom border-gray-light">
            {editSkillClicked ? (
              <>
                <Button
                  type="button"
                  onClick={() => (name ? handleEdit() : setHasNameError(true))}
                  className="Button ml-16 mb-16 mt-16"
                  customColor
                  disabled={
                    hasNameError ||
                    hasUniqueError ||
                    name?.length > 255 ||
                    newCategoryName?.length > 255 ||
                    (addCategory &&
                      newCategoryName?.length <= 0 &&
                      !selectedCategory) ||
                    (!addCategory &&
                      !newCategoryName?.length <= 0 &&
                      selectedCategory) ||
                    categoryCreationError
                  }
                >
                  {t('common.save_changes')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={() => {
                    setSuccessText(false);
                    setErrorText(false);
                    setSaveChangesError(false);
                    setEditSkillClicked(true);
                  }}
                  className="Button Button--linked ml-16 mb-16 mt-16 d-f jc-s"
                >
                  {t('skills.edit_skill')}
                </Button>
              </>
            )}
            <ButtonGroup alignEnd>
              <div
                onClick={handleDelete}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') && handleClose
                }
                role="button"
                tabIndex="0"
                className="d-f ai-c mt-4 mr-16"
              >
                <Icon name="delete" />
              </div>
              <div
                onClick={() => {
                  handleClose();
                  setChosenSkill({});
                }}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') && handleClose
                }
                role="button"
                tabIndex="0"
                className="d-f ai-c mt-4 mr-16"
              >
                <Icon name="close" className="c-midnight-50" />
              </div>
            </ButtonGroup>
          </div>
          <div className="px-16 pt-12 pb-8 border-bottom border-gray-light">
            {editSkillClicked ? (
              <>
                {saveChangesError && (
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    <div className="d-f fd-r mb-8 ai-c c-error">
                      <div>
                        <AlertCircle />
                      </div>
                      <div className="pl-8 pb-6">
                        {t('skills.changes_not_saved')}
                      </div>
                    </div>
                  </Text>
                )}
                <Text variant={TEXT_VARIANTS.BODY_BOLD}>
                  {`${t('skills.name_column')}`}
                  <span className="llui-text-instrux-error ml-4">*</span>
                </Text>
                <TextField
                  whiteBackground
                  ref={skillNameRef}
                  value={name}
                  onChange={e => {
                    reset();
                    setHasNameError(false);
                    setHasUniqueError(false);
                    setSaveChangesError(false);
                    setName(e.target.value);
                  }}
                />
                {(hasNameError || hasUniqueError) && (
                  <div className="SkillsLibrary-createTextErrorContainer">
                    <Error
                      message={
                        hasUniqueError
                          ? t('skills.unique_label_error')
                          : t('skills.empty_error')
                      }
                    />
                  </div>
                )}
                {name?.length > 255 && (
                  <div className="SkillsLibrary-createTextErrorContainer d-f jc-sb ai-c">
                    <Error message={t('skills.length_error')} />
                    <Text
                      variant={TEXT_VARIANTS.INSTRUX_ERROR}
                      className="mt-16"
                    >{`${name.length}/255`}</Text>
                  </div>
                )}
              </>
            ) : (
              <>
                {successText && (
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    <div className="d-f fd-r mb-8 ai-c c-success">
                      <div>
                        <Check />
                      </div>
                      <div className="pl-8 pb-6">
                        {t('skills.changes_saved')}
                      </div>
                    </div>
                  </Text>
                )}
                <div className="mb-12">
                  <Text variant={TEXT_VARIANTS.BODY_BOLD}>
                    {`${t('skills.name_column')}`}
                    <span className="llui-text-instrux-error ml-4">*</span>
                  </Text>
                </div>
                <div>
                  <Text variant={TEXT_VARIANTS.SUBHEADER_BOLD}>{name}</Text>
                </div>
              </>
            )}
          </div>
          <div className="border-bottom border-gray-light">
            <Accordion
              title={t('skills.skill_details')}
              minimal
              fullWidth
              whiteBackground
            >
              {editSkillClicked ? (
                <div className="pl-16 pr-16 w-100p d-f fd-c">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('common.description')}
                  </Text>
                  <DebouncedTextArea
                    id={t('skills.add_skill_description')}
                    name={t('skills.add_skill_description')}
                    className="h-300 w-100p mt-8 Modal-input"
                    rows="6"
                    onChange={value => {
                      setSaveChangesError(false);
                      setDescription(value);
                    }}
                    value={description}
                  />
                  <Text
                    variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
                    className="mt-16 mb-8"
                  >
                    {t('skills.category')}
                  </Text>
                  <Select
                    onChange={handleSelectChange}
                    defaultValue={category?.id}
                    ariaLabel={t('skills.category')}
                  >
                    <option key="empty" value={null} />
                    <option key="" value="0">
                      {t('skills.add_new_category')}
                    </option>
                    {skillsCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                  {errorText && (
                    <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                      <div className="d-f fd-r mt-8 c-error">
                        <div>
                          <AlertCircle />
                        </div>
                        <div className="pl-8">
                          {t('skills.changes_not_saved')}
                        </div>
                      </div>
                    </Text>
                  )}
                  {addCategory && (
                    <>
                      <Text
                        variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
                        className="mt-16"
                      >
                        {t('skills.add_a_new_category')}
                      </Text>
                      <TextField
                        whiteBackground
                        placeholder={t('skills.type_category_name')}
                        value={newCategoryName}
                        onChange={e => {
                          createCategoryReset();
                          setSaveChangesError(false);
                          setErrorText(false);
                          setCategoryCreationError(false);
                          setNewCategoryName(e.target.value);
                        }}
                      />
                      {newCategoryName.trim().length === 0 && (
                        <div className="SkillsLibrary-createTextErrorContainer">
                          <Error message={t('skills.empty_error')} />
                        </div>
                      )}
                      {newCategoryName?.length > 255 && (
                        <div className="SkillsLibrary-createTextErrorContainer d-f jc-sb ai-c">
                          <Error message={t('skills.length_error')} />
                          <Text
                            variant={TEXT_VARIANTS.INSTRUX_ERROR}
                            className="mt-16"
                          >{`${newCategoryName?.length}/255`}</Text>
                        </div>
                      )}
                      {categoryCreationError && (
                        <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                          <div className="d-f fd-r mt-16 c-error">
                            <div>
                              <AlertCircle />
                            </div>
                            <div className="pl-8 pt-2">
                              {t('skills.category_creation_error')}
                            </div>
                          </div>
                        </Text>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="pl-16 pr-16 w-100p d-f fd-c">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('common.description')}
                  </Text>
                  <Text variant={TEXT_VARIANTS.BODY_LIGHT} className="pl-10">
                    {description}
                  </Text>
                  <Text
                    variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
                    className="mt-16 mb-8"
                  >
                    {t('skills.category')}
                  </Text>
                  <Text variant={TEXT_VARIANTS.BODY_LIGHT} className="pl-10">
                    {category?.name}
                  </Text>
                </div>
              )}
            </Accordion>
          </div>
          {deleteSkill && (
            <DeleteModal
              closeDeleteModal={closeDeleteModal}
              formData={chosenSkill}
              setBannerProperties={setBannerProperties}
              deleteSkill={deleteSkill}
            />
          )}
        </div>
      )}
      {skillSliderOpen && (
        <div className="SkillsLibrary-editSlider w-360 d-f fd-c pos-a top-0 right-0 h-100 bg-white border-left border-gray-light">
          <div className="d-f jc-sb h-60 w-100p ai-c border-bottom border-gray-light">
            <Button
              type="button"
              onClick={() => (name ? saveChanges() : setHasNameError(true))}
              className="Button ml-16 mb-16 mt-16"
              customColor
            >
              {t('common.save_changes')}
            </Button>
            <div
              onClick={handleClose}
              onKeyDown={e =>
                (e.key === 'Enter' || e.key === ' ') && handleClose
              }
              role="button"
              tabIndex="0"
              className="d-f ai-c mt-4 mr-16"
            >
              <Icon name="close" className="c-midnight-50" />
            </div>
          </div>
          <div className="px-16 pt-16 pb-8 border-bottom border-gray-light">
            <Text variant={TEXT_VARIANTS.BODY_BOLD}>
              {`${t('skills.name_column')}`}
              <span className="llui-text-instrux-error ml-4">*</span>
            </Text>
            <TextField
              whiteBackground
              ref={skillNameRef}
              placeholder={t('skills.add_skill_name')}
              value={name}
              onChange={e => {
                setHasNameError(false);
                setHasUniqueError(false);
                setHasSaveError(true);
                setName(e.target.value);
              }}
              aria-label={t('skills.add_skill_name')}
            />
            {(hasNameError || hasUniqueError) && (
              <div className="SkillsLibrary-createTextErrorContainer">
                <Error
                  message={
                    hasUniqueError
                      ? t('skills.unique_error')
                      : t('skills.empty_error')
                  }
                />
              </div>
            )}
            {name?.length > 255 && (
              <div className="SkillsLibrary-createTextErrorContainer d-f jc-sb ai-c">
                <Error message={t('skills.length_error')} />
                <Text
                  variant={TEXT_VARIANTS.INSTRUX_ERROR}
                  className="mt-16"
                >{`${name.length}/255`}</Text>
              </div>
            )}
            <div>
              <Accordion
                title={t('skills.add_skill_details')}
                minimal
                fullWidth
                whiteBackground
              >
                <div className="pl-16 pr-16 w-100p d-f fd-c">
                  <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
                    {t('common.description')}
                  </Text>
                  <DebouncedTextArea
                    id={t('skills.add_skill_description')}
                    name={t('skills.add_skill_description')}
                    className="h-300 w-100p mt-8 Modal-input"
                    rows="6"
                    onChange={value => setDescription(value)}
                    placeholder={t('skills.add_skill_description')}
                    value={description}
                  />
                  <Text
                    variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
                    className="mt-16 mb-8"
                  >
                    {t('skills.category')}
                  </Text>
                  <Select
                    onChange={e => setCategory(e.target.value)}
                    value={category}
                    ariaLabel={t('skills.category')}
                  >
                    {[{ id: null }, ...skillsCategories].map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </Accordion>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SkillSlider;
